import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import FadeImg from "@/components/FadeImg";
import { Reveal, MaskedLines, Overline } from "@/components/Motion";

const CATEGORIES = ["All", "Residential", "Architecture", "Interiors", "Sustainable Architecture", "Research & Material Exploration", "Visualisation"];

export default function Architecture() {
  const [projects, setProjects] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const list = cat === "All" ? projects : projects.filter((p) => p.category === cat);

  return (
    <main data-testid="architecture-page" className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Overline>Work — Architecture</Overline>
      <MaskedLines
        className="mt-6 mb-10"
        lineClassName="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight"
        lines={["Architecture"]}
      />
      <Reveal className="mb-16">
        <div className="flex flex-wrap gap-2" data-testid="arch-category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`arch-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                cat === c ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/60 hover:border-brand hover:text-brand"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </Reveal>
      <div className="space-y-20">
        {list.map((p, i) => (
          <Reveal key={p.id}>
            <Link
              to={`/work/architecture/${p.id}`}
              data-testid={`arch-project-${p.id}`}
              className={`group grid grid-cols-1 md:grid-cols-12 gap-8 items-end ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
            >
              <div className={`md:col-span-8 ${i % 2 === 1 ? "md:[direction:ltr]" : ""}`}>
                <div className="frame-clip spotlight aspect-[16/10] bg-linen">
                  <FadeImg src={p.cover} alt={p.title} className="w-full h-full object-cover"
                    loading={i < 2 ? "eager" : "lazy"} fetchPriority={i < 2 ? "high" : undefined} />
                </div>
              </div>
              <div className={`md:col-span-4 ${i % 2 === 1 ? "md:[direction:ltr]" : ""}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">{p.category}</p>
                <h2 className="font-display text-3xl lg:text-5xl tracking-tight mt-3 group-hover:text-brand transition-colors">{p.title}</h2>
                <p className="mt-4 text-ink/60 leading-relaxed">{p.description}</p>
                <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-ink/50 border-t border-ink/15 pt-4">
                  {p.location} · {p.year} · {p.status}
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
        {!list.length && <p className="text-ink/50 font-mono text-sm">No projects in this category yet.</p>}
      </div>
    </main>
  );
}
